import React, { useEffect, useMemo, useState } from "react";

import { Modal, Pagination, Table } from "antd";
import moment from "moment";

import NoResults from "../../Common/NoResults";
import StyledScrollbar from "../../Common/UIComponents/StyledScrollbar";

import { convertUtcToLocal } from "../../Common/utils/dateUtils";

import { ReactComponent as IncompleteIcon } from "../../../assets/images/common/spec-flag-icons/IncompleteIcon.svg";
import { ReactComponent as InProcessIcon } from "../../../assets/images/common/spec-flag-icons/InProcessIcon.svg";
import { ReactComponent as InSpecIcon } from "../../../assets/images/common/spec-flag-icons/InSpecIcon.svg";
import { ReactComponent as OutOfSpecIcon } from "../../../assets/images/common/spec-flag-icons/OutOfSpecIcon.svg";
import emptyStateIcon from "../../../assets/images/EmptyState_V2.png";

import "./UploadDetailsModal.css";

const Statuses = {
  success: { color: "var(--color-content-positive)", icon: InSpecIcon, label: "Success" },
  partial: { color: "var(--color-primary)", icon: IncompleteIcon, label: "Partial" },
  pending: { color: "var(--color-content-warning)", icon: InProcessIcon, label: "Pending" },
  failed: { color: "var(--color-content-negative)", icon: OutOfSpecIcon, label: "Failed" },
};

const TABLE_PAGE_SIZE = 100;

function getFileNameFromPath(path) {
  return (path || "").split("/").pop() || "";
}

const getCategoryLabel = (sampleCategory) => {
  if (sampleCategory === "product") return "Product";
  if (sampleCategory === "environment") return "Environment";
  return "-";
};

function ModalHeader({ record }) {
  const fileName = getFileNameFromPath(record.s3_file_path);
  const category = getCategoryLabel(record.sample_category);
  const uploadedBy = record.requested_by || "-";
  const uploadedAt = record.request_date
    ? moment(new Date(convertUtcToLocal(record.request_date))).format("DD MMM YYYY | hh:mm a")
    : "-";
  const statusInfo = Statuses[record.status];
  const StatusIcon = statusInfo?.icon;

  return (
    <div className="UploadDetailsModal__Header">
      <span className="UploadDetailsModal__HeaderTitle">Upload Details</span>
      <div className="UploadDetailsModal__HeaderMetaItem">
        <span className="UploadDetailsModal__HeaderMetaLabel">File Name</span>
        <span className="UploadDetailsModal__HeaderMetaValue UploadDetailsModal__HeaderMetaValue--filename">{fileName || "-"}</span>
      </div>
      <div className="UploadDetailsModal__HeaderMeta">
        <div className="UploadDetailsModal__HeaderMetaItem">
          <span className="UploadDetailsModal__HeaderMetaLabel">Category</span>
          <span className="UploadDetailsModal__HeaderMetaValue">{category}</span>
        </div>
        <div className="UploadDetailsModal__HeaderMetaDivider" />
        <div className="UploadDetailsModal__HeaderMetaItem">
          <span className="UploadDetailsModal__HeaderMetaLabel">Uploaded By</span>
          <span className="UploadDetailsModal__HeaderMetaValue">{uploadedBy}</span>
        </div>
        <div className="UploadDetailsModal__HeaderMetaDivider" />
        <div className="UploadDetailsModal__HeaderMetaItem">
          <span className="UploadDetailsModal__HeaderMetaLabel">Uploaded At</span>
          <span className="UploadDetailsModal__HeaderMetaValue">{uploadedAt}</span>
        </div>
        <div className="UploadDetailsModal__HeaderMetaDivider" />
        <div className="UploadDetailsModal__HeaderMetaItem">
          <span className="UploadDetailsModal__HeaderMetaLabel">Status</span>
          {statusInfo ? (
            <span
              className={`UploadDetailsModal__HeaderMetaValue UploadDetailsModal__HeaderStatus${record.status === "partial" ? " UploadDetailsModal__HeaderStatus--partial" : ""}`}
              style={{ color: statusInfo.color }}
            >
              <StatusIcon aria-hidden />
              {statusInfo.label}
            </span>
          ) : (
            <span className="UploadDetailsModal__HeaderMetaValue">-</span>
          )}
        </div>
      </div>
    </div>
  );
}

const TESTS_COLUMNS = [
  {
    title: "Test",
    dataIndex: "test",
    key: "test",
    width: "50%",
  },
  {
    title: "Spec",
    dataIndex: "spec",
    key: "spec",
    width: "25%",
    render: (val) => (val === null || val === undefined ? "-" : String(val)),
  },
  {
    title: "Unit",
    dataIndex: "unit",
    key: "unit",
    width: "25%",
    render: (val) => (val === null || val === undefined ? "-" : String(val)),
  },
];

function OriginalDataExpanded({ originalData }) {
  if (!originalData || typeof originalData !== "object") return null;

  if (Array.isArray(originalData.fields)) {
    const fieldRows = originalData.fields.map((f, i) => ({ ...f, _key: i }));
    const fieldCols = [
      { title: "Field", dataIndex: "title_field", key: "title_field" },
      {
        title: "Value",
        dataIndex: "value",
        key: "value",
        render: (v) => (v === null || v === undefined ? "-" : String(v)),
      },
    ];
    return (
      <div className="UploadDetailsModal__ExpandedTests">
        <Table dataSource={fieldRows} columns={fieldCols} rowKey="_key" pagination={false} rootClassName="UploadDetailsModal__ExpandedTable" />
        {Array.isArray(originalData.tests) && originalData.tests.length > 0 && (
          <Table
            dataSource={originalData.tests.map((t, i) => ({ ...t, _key: i }))}
            columns={TESTS_COLUMNS}
            rowKey="_key"
            pagination={false}
            rootClassName="UploadDetailsModal__ExpandedTable"
          />
        )}
      </div>
    );
  }

  const entries = Object.entries(originalData);
  if (!entries.length) return null;
  const flatData = entries.map(([k, v], i) => ({
    _key: i,
    field: k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
    value: v === null || v === undefined ? "-" : String(v),
  }));
  return (
    <div className="UploadDetailsModal__ExpandedTests">
      <Table
        dataSource={flatData}
        columns={[
          { title: "Field", dataIndex: "field", key: "field" },
          { title: "Value", dataIndex: "value", key: "value" },
        ]}
        rowKey="_key"
        pagination={false}
        rootClassName="UploadDetailsModal__ExpandedTable"
      />
    </div>
  );
}

function MappingIssuesTable({ data }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(TABLE_PAGE_SIZE);

  const normalizedData = useMemo(() => data.map((row, i) => {
    if (row === null || typeof row !== "object") {
      return { message: String(row), _rowKey: i };
    }
    return { ...row, _rowKey: i };
  }), [data]);

  useEffect(() => {
    setCurrentPage(1);
  }, [normalizedData]);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return normalizedData.slice(start, start + pageSize);
  }, [normalizedData, currentPage, pageSize]);

  if (!normalizedData.length) return null;

  const columns = Object.keys(normalizedData[0])
    .filter((k) => !k.startsWith("_") && k !== "original_data")
    .map((key) => ({
      title: key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
      dataIndex: key,
      key,
      render: (val) => {
        if (val === null || val === undefined) return "-";
        if (Array.isArray(val)) return val.join(", ");
        if (typeof val === "object") return JSON.stringify(val);
        return String(val);
      },
    }));
  const isRowExpandable = (record) => {
    const originalData = record._original_data ?? record.original_data;
    return !!originalData && typeof originalData === "object" && Object.keys(originalData).length > 0;
  };
  const hasExpandableRows = normalizedData.some(isRowExpandable);
  return (
    <>
      <Table
        dataSource={paginatedData}
        columns={columns}
        rowKey="_rowKey"
        pagination={false}
        rootClassName="UploadDetailsModal__Table"
        expandable={hasExpandableRows ? {
          expandedRowRender: (record) => <OriginalDataExpanded originalData={record._original_data ?? record.original_data} />,
          rowExpandable: isRowExpandable,
        } : undefined}
      />
      <Pagination
        className="UploadDetailsModal__Pagination"
        current={currentPage}
        pageSize={pageSize}
        total={normalizedData.length}
        size="small"
        showSizeChanger
        pageSizeOptions={[TABLE_PAGE_SIZE]}
        onChange={(page, size) => {
          setCurrentPage(page);
          setPageSize(size);
        }}
      />
    </>
  );
}

/** Builds a flat table where each row is one parsed sample.
 *  Columns = all unique title_field values across every sample.
 *  Expandable rows reveal the tests for that sample.
 */
function ParsedDataTable({ parsedSamples }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(TABLE_PAGE_SIZE);

  // Collect all unique title_field keys (preserving first-seen order)
  const titleFields = useMemo(() => [
    ...new Set(
      parsedSamples.flatMap((s) => (Array.isArray(s.fields) ? s.fields.map((f) => f.title_field) : [])),
    ),
  ], [parsedSamples]);

  const fieldsColumns = useMemo(() => titleFields.map((title) => ({
    title,
    dataIndex: title,
    key: title,
    render: (val) => (val === null || val === undefined ? "-" : String(val)),
  })), [titleFields]);

  // Each row holds the flattened field values + the tests array for expansion
  const dataSource = useMemo(() => parsedSamples.map((sample, i) => {
    const row = { _rowKey: i, _tests: Array.isArray(sample.tests) ? sample.tests : [] };
    (Array.isArray(sample.fields) ? sample.fields : []).forEach((f) => {
      row[f.title_field] = f.value;
    });
    return row;
  }), [parsedSamples]);

  useEffect(() => {
    setCurrentPage(1);
  }, [dataSource]);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return dataSource.slice(start, start + pageSize);
  }, [dataSource, currentPage, pageSize]);

  if (!parsedSamples.length) {
    return (
      <NoResults image={emptyStateIcon} message="No parsed data available" className="UploadDetailsModal__Empty" />
    );
  }

  return (
    <>
      <Table
        dataSource={paginatedData}
        columns={fieldsColumns}
        rowKey="_rowKey"
        pagination={false}
        rootClassName="UploadDetailsModal__Table"
        expandable={{
          expandedRowRender: (record) => (
            <div className="UploadDetailsModal__ExpandedTests">
              {record._tests && record._tests.length > 0 ? (
                <Table
                  dataSource={record._tests.map((t, i) => ({ ...t, _key: i }))}
                  columns={TESTS_COLUMNS}
                  rowKey="_key"
                  pagination={false}
                  rootClassName="UploadDetailsModal__ExpandedTable"
                />
              ) : (
                <p className="UploadDetailsModal__NoTestData">No test data</p>
              )}
            </div>
          ),
          rowExpandable: () => true,
        }}
      />
      <Pagination
        className="UploadDetailsModal__Pagination"
        current={currentPage}
        pageSize={pageSize}
        total={dataSource.length}
        size="small"
        showSizeChanger
        pageSizeOptions={[TABLE_PAGE_SIZE]}
        onChange={(page, size) => {
          setCurrentPage(page);
          setPageSize(size);
        }}
      />
    </>
  );
}

export default function UploadDetailsModal({ record, onClose }) {
  if (!record) return null;

  const {
    ml_parsed_insert_payload,
    ml_api_response,
    insert_api_response,
    failure_reason,
  } = record;

  let parsedSamples = [];
  if (Array.isArray(ml_parsed_insert_payload?.data)) {
    parsedSamples = ml_parsed_insert_payload.data;
  } else if (Array.isArray(ml_api_response?.data)) {
    parsedSamples = ml_api_response.data;
  }

  const apiResponse = (insert_api_response && typeof insert_api_response === "object") ? insert_api_response : {};

  const failedUploadResult = Array.isArray(apiResponse.failed_upload_result)
    ? apiResponse.failed_upload_result
    : [];

  // Two response shapes, normalized to the same {index, original_data, missing_info} row shape:
  // Case A — upload attempted: failed_mapping[] is a top-level key
  // Case B — mapping failed before upload: result[].missing_info holds per-row errors
  let failedMappingData = [];
  if (Array.isArray(apiResponse.failed_mapping) && apiResponse.failed_mapping.length > 0) {
    failedMappingData = apiResponse.failed_mapping;
  } else if (Array.isArray(apiResponse.result)) {
    failedMappingData = apiResponse.result
      .filter((item) => Array.isArray(item.missing_info) && item.missing_info.length > 0)
      .map((item) => ({
        index: item.index,
        missing_info: item.missing_info,
        _original_data: item.original_data || null,
      }));
  }

  const hasApiIssues = !!failure_reason || failedUploadResult.length > 0 || failedMappingData.length > 0;

  return (
    <Modal
      open={!!record}
      maskClosable={false}
      closable
      centered
      destroyOnClose
      footer={null}
      width="55vw"
      wrapClassName="UploadDetailsModal"
      onCancel={onClose}
      title={<ModalHeader record={record} />}
    >
      <StyledScrollbar
        disableTrackYWidthCompensation
        disableTracksWidthCompensation
        id="uploadDetailsScrollBar"
        className="UploadDetailsModal__ScrollBar"
      >
        <div className="UploadDetailsModal__Content">

          {/* Section 1:Parsed Data — one row per sample, expandable tests */}
          <div className="UploadDetailsModal__Section">
            <p className="UploadDetailsModal__SectionTitle">Parsed Data</p>
            <ParsedDataTable parsedSamples={parsedSamples} />
          </div>

          {/* Section 2: Issues */}
          <div className="UploadDetailsModal__Section">
            <p className="UploadDetailsModal__SectionTitle">Issues</p>

            {!hasApiIssues && (
              <NoResults image={emptyStateIcon} message="No issues recorded" className="UploadDetailsModal__Empty" />
            )}

            {/* Top-level failure message */}
            {!!failure_reason && (
              <p className="UploadDetailsModal__FailureReason">{failure_reason}</p>
            )}

            {/* Failed records — string list from failed_upload_result */}
            {failedUploadResult.length > 0 && (
              <div className="UploadDetailsModal__SubSection">
                <p className="UploadDetailsModal__SubSectionTitle">Failed Records</p>
                <ol className="UploadDetailsModal__FailureList">
                  {failedUploadResult.map((msg, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <li key={i} className="UploadDetailsModal__FailureItem">{String(msg)}</li>
                  ))}
                </ol>
              </div>
            )}

            {/* Mapping issues — only rendered when non-empty */}
            {failedMappingData.length > 0 && (
              <MappingIssuesTable data={failedMappingData} />
            )}
          </div>

        </div>
      </StyledScrollbar>
    </Modal>
  );
}
